import { createFileRoute } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { BlogArticle, articleHead, type Article } from "@/components/marketing/BlogArticle";

const article: Article = {
  path: "/blog/ai-customer-support-automation",
  category: "Guide",
  readTime: "8 min read",
  published: "2026-07-22",
  title: "AI customer support automation",
  titleEm: "without the robotic feeling.",
  lead: "Support automation earned a bad name because most of it deflects customers into dead ends. Done right — grounded, honest, and with a clean human handoff — it resolves the routine instantly and makes your team look great. Here's how.",
  metaDescription:
    "How AI customer support automation works in B2B: what to automate, what to escalate, and how to keep answers grounded in your own documentation.",
  takeaways: [
    "Automate the repetitive 60–80%, escalate the rest with full context.",
    "Ground every answer in your real docs; never let the AI guess.",
    "Measure resolution quality and CSAT, not just deflection rate.",
    "A good handoff is what customers actually remember.",
  ],
  blocks: [
    { type: "h2", text: "Why most support automation feels bad" },
    {
      type: "p",
      text: "The classic failure is optimizing for deflection: the bot's job becomes preventing a human conversation. Customers feel it immediately — looping menus, 'I didn't understand that', links to articles they already read. The fix isn't less automation, it's honest automation that resolves what it can and gets out of the way fast when it can't.",
    },
    { type: "h2", text: "The three tiers of a support request" },
    {
      type: "ul",
      items: [
        <>
          <strong className="text-foreground">Instant (60–80%):</strong> password resets, pricing,
          policy, 'how do I…' — grounded answers the AI resolves in one message.
        </>,
        <>
          <strong className="text-foreground">Assisted:</strong> needs a lookup or a small action;
          the AI gathers everything, then loops in a human with the case pre-written.
        </>,
        <>
          <strong className="text-foreground">Human:</strong> judgment, empathy, edge cases — routed
          instantly with the full transcript and a suggested resolution.
        </>,
      ],
    },
    {
      type: "callout",
      title: "The golden rule",
      text: "If the AI can't ground an answer in your knowledge base, it should say so and offer a human — never invent. Hallucinated support answers cost more trust than they save time.",
    },
    { type: "h2", text: "What to automate first" },
    {
      type: "p",
      text: "Start with your top 20 tickets. Pull them from your helpdesk, group by theme, and point the AI Twin at the docs that answer them. That single move usually covers the majority of volume — because support demand is deeply repetitive.",
    },
    { type: "h2", text: "Getting the handoff right" },
    {
      type: "p",
      text: "The moment automation escalates is the moment customers judge you. A good handoff carries the whole context: who the customer is, what they tried, what the AI already answered, and the one open question. The human opens the ticket already knowing the story — no 'can you repeat that?'.",
    },
    { type: "h2", text: "Metrics that actually matter" },
    {
      type: "table",
      head: ["Vanity metric", "What to track instead"],
      rows: [
        ["Deflection rate", "First-contact resolution quality"],
        ["Chats handled", "CSAT on automated conversations"],
        ["Response time", "Time-to-resolution end to end"],
        ["Coverage hours", "% of routine resolved without a human"],
      ],
    },
    { type: "h2", text: "Where an AI Twin fits" },
    {
      type: "p",
      text: "An AI Twin is support automation plus a growth engine: the same grounded model that resolves a how-to question also spots buying intent, captures the lead, and routes it to sales. You automate support and feed pipeline from the same conversation — one system instead of two.",
    },
  ],
  faqs: [
    {
      q: "What is AI customer support automation?",
      a: "It's using an AI agent grounded in your help docs to resolve routine customer questions instantly and escalate the rest to a human with full context. Done well it handles the repetitive 60–80% and improves resolution quality.",
    },
    {
      q: "Will AI support automation frustrate my customers?",
      a: "Only if it's built to deflect. If it answers accurately from your real docs, admits when it can't, and hands off cleanly with context, customers get faster resolutions and rate it higher than menu-based bots.",
    },
    {
      q: "How much support volume can be automated?",
      a: "For most teams, 60–80% of tickets are repetitive enough to resolve instantly when the AI is grounded in good docs. The remaining cases are exactly the ones your humans should be spending time on.",
    },
  ],
  related: [
    { slug: "what-is-an-ai-twin", title: "What is an AI Twin? The complete 2026 guide" },
    {
      slug: "convert-website-visitors-with-ai",
      title: "How to convert more website visitors with AI",
    },
    { slug: "ai-lead-qualification", title: "AI lead qualification for B2B: a 2026 playbook" },
  ],
};

export const Route = createFileRoute("/blog/ai-customer-support-automation")({
  head: () =>
    articleHead(
      article,
      buildSeo({
        path: article.path,
        title: "AI Customer Support Automation: 2026 Guide | Reality Twin",
        description: article.metaDescription ?? article.lead,
        type: "article",
      }),
    ),
  component: () => <BlogArticle article={article} />,
});
